import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './MyTransactions.css';

function MyTransactions({ user, onBack }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, week, month
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [user.id]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const servicesRef = collection(db, 'serviceRecords');
      const q = query(
        servicesRef,
        where('technicianId', '==', user.id),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setTransactions(records);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTransactions = () => {
    let filtered = [...transactions];

    // Apply time filter
    const now = new Date();
    if (filter === 'today') {
      const startOfDay = new Date(now.setHours(0, 0, 0, 0));
      filtered = filtered.filter(t => {
        const createdAt = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
        return createdAt >= startOfDay;
      });
    } else if (filter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => {
        const createdAt = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
        return createdAt >= weekAgo;
      });
    } else if (filter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => {
        const createdAt = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
        return createdAt >= monthAgo;
      });
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.outletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { label: 'Completed', class: 'status-completed' },
      pending: { label: 'Pending', class: 'status-pending' },
      in_progress: { label: 'In Progress', class: 'status-progress' }
    };
    return badges[status] || badges.completed;
  };

  const getServiceTypeIcon = (type) => {
    const icons = {
      routine: '🔧',
      emergency: '🚨',
      other: '📋'
    };
    return icons[type] || '📋';
  };

  const filteredTransactions = getFilteredTransactions();

  // Calculate stats
  const stats = {
    total: transactions.length,
    today: transactions.filter(t => {
      const createdAt = t.createdAt?.toDate ? t.createdAt.toDate() : new Date(t.createdAt);
      const today = new Date();
      return createdAt.toDateString() === today.toDateString();
    }).length,
    completed: transactions.filter(t => t.status === 'completed').length
  };

  return (
    <div className="my-transactions">
      {/* Header */}
      <div className="page-header">
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
        <h1>My Transactions</h1>
      </div>

      {/* Stats Cards */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Services</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <div className="stat-value">{stats.today}</div>
            <div className="stat-label">Today</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✓</div>
          <div className="stat-info">
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'today' ? 'active' : ''}`}
            onClick={() => setFilter('today')}
          >
            Today
          </button>
          <button
            className={`filter-btn ${filter === 'week' ? 'active' : ''}`}
            onClick={() => setFilter('week')}
          >
            This Week
          </button>
          <button
            className={`filter-btn ${filter === 'month' ? 'active' : ''}`}
            onClick={() => setFilter('month')}
          >
            This Month
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by outlet, serial, or order #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Transactions List */}
      <div className="transactions-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No transactions found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search or filters'
                : 'Start recording services to see them here'}
            </p>
          </div>
        ) : (
          <div className="transactions-list">
            {filteredTransactions.map((transaction) => (
              <div key={transaction.id} className="transaction-card">
                <div className="transaction-header">
                  <div className="transaction-number">
                    <span className="order-badge">#{transaction.orderNumber || 'N/A'}</span>
                    <span className={`status-badge ${getStatusBadge(transaction.status).class}`}>
                      {getStatusBadge(transaction.status).label}
                    </span>
                  </div>
                  <div className="transaction-date">
                    {formatDate(transaction.createdAt)}
                  </div>
                </div>

                <div className="transaction-body">
                  <div className="transaction-main">
                    <div className="service-type-icon">
                      {getServiceTypeIcon(transaction.serviceType)}
                    </div>
                    <div className="transaction-details">
                      <h3 className="outlet-name">{transaction.outletName}</h3>
                      <div className="transaction-meta">
                        <span className="meta-item">
                          {transaction.equipmentType === 'keg_pump' ? '☕ Keg Pump' : '🍺 Draft Machine'}
                        </span>
                        <span className="meta-divider">•</span>
                        <span className="meta-item">{transaction.serialNumber}</span>
                      </div>
                    </div>
                  </div>

                  {transaction.faultDescription && (
                    <div className="transaction-description">
                      <p>{transaction.faultDescription}</p>
                    </div>
                  )}

                  {transaction.drinkTypes && transaction.drinkTypes.length > 0 && (
                    <div className="drink-types">
                      {transaction.drinkTypes.map((drink, index) => (
                        <span key={index} className="drink-tag">{drink}</span>
                      ))}
                    </div>
                  )}

                  {transaction.partsUsed && (
                    <div className="parts-used">
                      <span className="parts-icon">🔧</span>
                      <span className="parts-text">{transaction.partsUsed}</span>
                    </div>
                  )}

                  {transaction.location && (
                    <div className="location-info">
                      <span className="location-icon">📍</span>
                      <span className="location-text">
                        {transaction.location.latitude.toFixed(6)}, {transaction.location.longitude.toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTransactions;
